import React, { useState } from 'react';
import Header from '../common/Header';
import { getApiUrl } from '../../utils/apiConfig';
import { supabase } from '../../utils/supabase';

const colorPalette = {
  textDefault: '#000000',
  borderDefault: '#D9D9D9',
  borderBrand: '#000000',
  c7ab7d: '#C7AB7D',
  f0f9d6: '#F0F9D6',
  black: '#000000',
  greenDark: '#2C5057',
  greenMid: '#557257',
  white: '#FFFFFF',
};

const ParentProfileForm = () => {
  const [formData, setFormData] = useState({
    studentName: '',
    school: '',
    currentGrade: '',
    relation: '',
    learningDetails: '',
    rewardContribution: '',
    iepUpload: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, iepUpload: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (loading || submitted) {
      console.log('⚠️ Form submission already in progress or completed, ignoring duplicate click');
      return;
    }

    setLoading(true);
    setError('');

    // Simple validation
    if (
      !formData.studentName ||
      !formData.school ||
      !formData.currentGrade ||
      !formData.relation ||
      !formData.learningDetails ||
      !formData.rewardContribution
    ) {
      setError('Please fill out all required fields.');
      setLoading(false);
      return;
    }

    try {
      // Get the user_id from Supabase session (user is logged in)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      let userId, username;
      
      if (sessionError || !session?.user?.id) {
        // Fall back to localStorage (for signup flow)
        const signupUserId = localStorage.getItem('signupUserId');
        if (!signupUserId) {
          throw new Error('No user ID found. Please log in or complete signup first.');
        }
        userId = signupUserId;
        username = localStorage.getItem('signupUsername') || 'Unknown';
        console.log('🔑 Using user_id from localStorage (signup flow):', userId);
      } else {
        // User is logged in, use Supabase user ID
        userId = session.user.id;
        const userMetadata = session.user.user_metadata || {};
        username = userMetadata.username || localStorage.getItem('signupUsername') || session.user.email?.split('@')[0] || 'Unknown';
        console.log('🔑 Using user_id from Supabase session:', userId);
      }
      
      console.log('👤 Using username:', username);

      // Send parent profile data to backend
      console.log('🔄 Sending parent profile data to backend...');
      const response = await fetch(getApiUrl('parent-profiles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          Parent_ID: userId, // Use same as user_id for consistency
          yourStudentName: formData.studentName,
          studentsSchool: formData.school,
          studentsCurrentGrade: formData.currentGrade,
          relationshipToStudent: formData.relation,
          learningDifferences: formData.learningDetails,
          levelOfCondition: formData.rewardContribution,
          learningPreferences: formData.learningDetails, // Map to existing field
          username: username
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          // Profile already exists, continue anyway
          console.log('⚠️ Parent profile already exists, continuing to parent interface...');
          setSubmitted(true);
          localStorage.removeItem('signupUserId');
          localStorage.removeItem('signupUsername');
          window.location.href = '/parent';
          return;
        }
        throw new Error(errorData.error || 'Failed to save parent profile');
      }

      const result = await response.json();
      console.log('✅ Parent profile saved to backend:', result);

      // Mark as submitted to prevent duplicate submissions
      setSubmitted(true);

      // Clear the stored user_id and username to prevent reuse
      localStorage.removeItem('signupUserId');
      localStorage.removeItem('signupUsername');
      console.log('🧹 Cleared signupUserId and signupUsername from localStorage');

      // Redirect to parent interface
      console.log('🎯 Redirecting to parent interface...');
      window.location.href = '/parent';

    } catch (error) {
      console.error('Error saving parent profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      height: '100vh',
      background: 'linear-gradient(270deg, #44746d 5%, #283D40 97%)',
      fontFamily: 'Arial, sans-serif',
      color: colorPalette.textDefault,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Header />
      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '40px',
        maxWidth: '1600px',
        margin: '0 auto',
        width: '100%',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h1 style={{
            color: colorPalette.f0f9d6,
            fontSize: '40px',
            fontWeight: '700',
            marginBottom: '20px'
          }}>
            Welcome Parent!
          </h1> <br/>
          <p style={{
            color: colorPalette.f0f9d6,
            fontSize: '22px',
            lineHeight: '1.5',
            maxWidth: '900px',
            margin: '0 auto',
            fontWeight: 500
          }}>
            Thank you for trusting us to empower your student.<br/>
             Please introduce your child's learning challenges, preferences, and anything else you would like to share about your child.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'start',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {/* Left Column */}
          <div>
            {error && (
              <div style={{
                background: 'rgba(220, 38, 38, 0.9)',
                color: colorPalette.white,
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '16px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>{error}</div>
            )}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Your Student's Name</label>
              <input
                type="text"
                name="studentName"
                value={formData.studentName}
                onChange={handleInputChange}
                required
                style={{
                  width: '80%',
                  padding: '14px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Student's School</label>
              <input
                type="text"
                name="school"
                value={formData.school}
                onChange={handleInputChange}
                required
                style={{
                  width: '80%',
                  padding: '14px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Student's Current Grade</label>
              <input
                type="text"
                name="currentGrade"
                value={formData.currentGrade}
                onChange={handleInputChange}
                required
                style={{
                  width: '80%',
                  padding: '14px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Relationship to Student</label>
              <input
                type="text"
                name="relation"
                value={formData.relation}
                onChange={handleInputChange}
                required
                style={{
                  width: '80%',
                  padding: '14px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}> IEP or 504 Plan</label>
              <label htmlFor="iepUpload" style={{
                display: 'inline-block',
                padding: '10px 100px',
                background: colorPalette.f0f9d6,
                color: colorPalette.c7ab7d,
                fontSize: '18px',
                fontWeight: '600',
                borderRadius: '8px',
                cursor: 'pointer',
                border: `2px solid ${colorPalette.borderDefault}`,
                textAlign: 'center',
                transition: 'all 0.2s ease'
              }}>
                {formData.iepUpload ? formData.iepUpload.name : 'Upload'}
              </label>
              <input
                id="iepUpload"
                type="file"
                name="iepUpload"
                onChange={handleFileChange}
                style={{
                  display: 'none'
                }}
              />
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Your student's learning differences & level of condition.</label>
              <input
                type="text"
                name="learningDetails"
                value={formData.learningDetails}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '70px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Please share your student's learning preferences, favorite rewards, frequent breaks, and anything else to make ITZ a personalized learning experience.</label>
              <input
                type="text"
                name="rewardContribution"
                value={formData.rewardContribution}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '50px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
          </div>
        </form>

        <div style={{ 
          maxWidth: '1200px', 
          margin: '-100px auto 0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px'
        }}>
          <div></div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="submit"
              className="app-btn"
              onClick={handleSubmit}
              disabled={loading || submitted}
              style={{
                background: colorPalette.greenMid, 
                color: colorPalette.c7ab7d,
                border: 'default',
                padding: '18px 48px',
                borderRadius: '10px',
                fontSize: '22px',
                fontWeight: '700',
                cursor: (loading || submitted) ? 'not-allowed' : 'pointer',
                opacity: (loading || submitted) ? 0.6 : 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
              }}
            >
              {submitted ? 'Profile Submitted!' : loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentProfileForm;